import { companyData } from '@/mocks/company';
import { defaultTemplateConfig } from '@/mocks/template-config';
import { findPresetsByCategory } from '@/mocks/template-presets';
import type { TemplateConfig } from '@/mocks/template-config';

interface PDFBackCoverProps {
  config?: TemplateConfig;
  category?: string;
}

const DEFAULT_COVER_IMAGE = 'https://readdy.ai/api/search-image?query=Modern%20luxury%20house%20at%20dusk%20with%20white%20security%20surveillance%20camera%20mounted%20on%20exterior%20wall%20in%20foreground%2C%20warm%20golden%20ambient%20lighting%20from%20house%20windows%2C%20dark%20navy%20blue%20evening%20sky%2C%20professional%20security%20system%20installation%20scene%2C%20elegant%20contemporary%20architecture%20with%20glass%20and%20concrete%2C%20subtle%20landscape%20garden%20lighting%2C%20premium%20residential%20security%20atmosphere%2C%20photorealistic%20architectural%20photography%20style%2C%20dark%20moody%20tones%20with%20golden%20warm%20light%20accents%2C%20high%20contrast%20between%20camera%20and%20house%20background%2C%20clean%20professional%20composition%20suitable%20for%20commercial%20proposal%20cover&width=1400&height=900&seq=cover-bg-2026&orientation=landscape';

function getBackCoverImageByCategory(category?: string): string {
  if (!category) return DEFAULT_COVER_IMAGE;
  try {
    const presets = findPresetsByCategory(category as Parameters<typeof findPresetsByCategory>[0]);
    const active = presets.find((p) => p.active);
    return active?.pdfBackCoverImage || DEFAULT_COVER_IMAGE;
  } catch {
    return DEFAULT_COVER_IMAGE;
  }
}

export default function PDFBackCover({ config, category }: PDFBackCoverProps) {
  const cfg = config || defaultTemplateConfig;
  const activeSpecs = cfg.specialties.filter((s) => s.active).sort((a, b) => a.order - b.order);
  const COVER_IMAGE = getBackCoverImageByCategory(category);

  return (
    <div
      style={{
        width: '210mm',
        height: '297mm',
        position: 'relative',
        overflow: 'hidden',
        fontFamily: "'Segoe UI', Arial, sans-serif",
        boxSizing: 'border-box',
      }}
    >
      {/* ===== TOPO AZUL ESCURO ===== */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '72mm',
          background: 'linear-gradient(180deg, #0a1628 0%, #0f1f35 100%)',
          zIndex: 10,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 20mm',
        }}
      >
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
          <div
            style={{
              width: '38px',
              height: '38px',
              borderRadius: '50%',
              border: '2px solid #c9a44a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <span
              style={{
                color: '#c9a44a',
                fontSize: '20px',
                fontWeight: 700,
                fontFamily: 'Georgia, serif',
                lineHeight: 1,
              }}
            >
              E
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
            <span
              style={{
                color: '#ffffff',
                fontSize: '26px',
                fontWeight: 700,
                letterSpacing: '0.02em',
              }}
            >
              Eletrônica
            </span>
            <span
              style={{
                color: '#c9a44a',
                fontSize: '26px',
                fontWeight: 700,
                letterSpacing: '0.02em',
              }}
            >
              2000
            </span>
          </div>
        </div>

        {/* DESDE 1990 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
          <div style={{ width: '50px', height: '1px', backgroundColor: '#c9a44a' }} />
          <span
            style={{
              color: '#c9a44a',
              fontSize: '11px',
              letterSpacing: '0.35em',
              textTransform: 'uppercase',
              fontWeight: 500,
            }}
          >
            DESDE {companyData.foundedYear}
          </span>
          <div style={{ width: '50px', height: '1px', backgroundColor: '#c9a44a' }} />
        </div>

        {/* Título */}
        <h1
          style={{
            color: '#ffffff',
            fontSize: '30px',
            fontFamily: "Georgia, 'Times New Roman', serif",
            fontWeight: 400,
            letterSpacing: '0.08em',
            textAlign: 'center',
            margin: 0,
            lineHeight: 1.15,
            textTransform: 'uppercase',
          }}
        >
          OBRIGADO
        </h1>
        <p
          style={{
            color: '#c9a44a',
            fontSize: '14px',
            letterSpacing: '0.04em',
            textAlign: 'center',
            margin: '6px 0 0 0',
            fontWeight: 400,
          }}
        >
          Pela confiança em nosso trabalho
        </p>
      </div>

      {/* ===== FAIXA CURVA DOURADA ===== */}
      <div
        style={{
          position: 'absolute',
          top: '71mm',
          left: '-10%',
          right: '-10%',
          height: '6mm',
          background: '#0a1628',
          borderRadius: '0 0 50% 50%',
          borderBottom: '2px solid #c9a44a',
          zIndex: 15,
        }}
      />

      {/* ===== IMAGEM DA CASA ===== */}
      <div
        style={{
          position: 'absolute',
          top: '75mm',
          left: 0,
          right: 0,
          height: '95mm',
          zIndex: 5,
          overflow: 'hidden',
        }}
      >
        <img
          src={COVER_IMAGE}
          alt="Contracapa"
          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
          crossOrigin="anonymous"
        />
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '30mm',
            background: 'linear-gradient(to top, #0a1628 0%, transparent 100%)',
          }}
        />
      </div>

      {/* ===== CAIXA DE AGRADECIMENTO ===== */}
      <div
        style={{
          position: 'absolute',
          top: '132mm',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '150mm',
          zIndex: 20,
          backgroundColor: '#ffffff',
          borderRadius: '10px',
          padding: '18px 22px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          border: '1px solid rgba(201,164,74,0.3)',
          textAlign: 'center',
        }}
      >
        <h2
          style={{
            color: '#0a1628',
            fontSize: '18px',
            fontWeight: 700,
            margin: '0 0 10px 0',
          }}
        >
          Agradecemos sua preferência
        </h2>
        <div style={{ width: '60px', height: '2px', backgroundColor: '#c9a44a', margin: '0 auto 10px auto' }} />
        <p
          style={{
            color: '#444444',
            fontSize: '12px',
            lineHeight: 1.6,
            margin: 0,
          }}
        >
          Entre em contato para mais informações ou para dar início ao seu projeto.
          <br />
          Estamos prontos para atendê-lo com excelência.
        </p>
      </div>

      {/* ===== PARTE INFERIOR ===== */}
      <div
        style={{
          position: 'absolute',
          top: '170mm',
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(180deg, #0a1628 0%, #070f1a 100%)',
          zIndex: 10,
          padding: '0 16mm',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Logo grande */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
          <div
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              border: '2px solid #c9a44a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <span
              style={{
                color: '#c9a44a',
                fontSize: '24px',
                fontWeight: 700,
                fontFamily: 'Georgia, serif',
                lineHeight: 1,
              }}
            >
              E
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ color: '#ffffff', fontSize: '22px', fontWeight: 700, lineHeight: 1.1 }}>
              Eletrônica
            </span>
            <span style={{ color: '#c9a44a', fontSize: '22px', fontWeight: 700, lineHeight: 1.1 }}>
              2000
            </span>
          </div>
        </div>

        {/* Contatos */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', textAlign: 'center', marginBottom: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
            <div style={{ width: '14px', height: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <i className="ri-whatsapp-line" style={{ color: '#c9a44a', fontSize: '12px' }} />
            </div>
            <span style={{ color: '#e8e8e8', fontSize: '12px' }}>{companyData.phone1}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
            <div style={{ width: '14px', height: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <i className="ri-whatsapp-line" style={{ color: '#c9a44a', fontSize: '12px' }} />
            </div>
            <span style={{ color: '#e8e8e8', fontSize: '12px' }}>{companyData.phone2}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
            <div style={{ width: '14px', height: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <i className="ri-mail-line" style={{ color: '#c9a44a', fontSize: '12px' }} />
            </div>
            <span style={{ color: '#e8e8e8', fontSize: '12px' }}>{companyData.email}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
            <div style={{ width: '14px', height: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <i className="ri-global-line" style={{ color: '#c9a44a', fontSize: '12px' }} />
            </div>
            <span style={{ color: '#e8e8e8', fontSize: '12px' }}>{companyData.website}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
            <div style={{ width: '14px', height: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <i className="ri-map-pin-line" style={{ color: '#c9a44a', fontSize: '12px' }} />
            </div>
            <span style={{ color: '#e8e8e8', fontSize: '12px' }}>
              {companyData.city} – {companyData.state}
            </span>
          </div>
        </div>

        <div style={{ width: '80px', height: '1px', backgroundColor: 'rgba(201,164,74,0.4)', marginBottom: '14px' }} />

        {/* Especialidades */}
        {activeSpecs.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '14px' }}>
            {activeSpecs.map((spec, index) => (
              <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                <div style={{ width: '14px', height: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <i className={`${spec.icon}`} style={{ color: '#c9a44a', fontSize: '11px' }} />
                </div>
                <span style={{ color: '#9ca3af', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                  {spec.name}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Slogan */}
        <p style={{ color: '#c9a44a', fontSize: '11px', textAlign: 'center', margin: 0 }}>
          {companyData.name} – Tradição, tecnologia e confiança desde {companyData.foundedYear}.
        </p>
      </div>
    </div>
  );
}